/**
 * Settings → Users & Roles → Member Detail (Step 4 of in-app user
 * management).
 *
 * Route: /settings/users/:user_id
 *
 * Three stacked sections + a right-side actions panel:
 *   1. Identity     — avatar, name, email, role, status, joined,
 *                     last active.
 *   2. Activity     — recent_actions from tenant_admin_event (this
 *                     user's actions inside this tenant), filterable
 *                     by date range.
 *   3. Multi-org    — OTHER Clerk Orgs this user belongs to + their
 *                     role in each. Visible to Tenant Admin AND HR
 *                     Director per the spec clarification. Hidden
 *                     below.
 *
 * Actions (right panel): Change role / Suspend|Unsuspend / Remove.
 * Every action opens the corresponding modal from UsersRolesPage —
 * imported here to avoid duplication. Self-protection is honored
 * via the same isSelf check.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useUser } from "@clerk/clerk-react";
import {
  AlertTriangle,
  Building2,
  Calendar,
  ChevronLeft,
  Mail,
  Pause,
  Play,
  ShieldCheck,
  Trash2,
  UserCog,
} from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useParams } from "react-router-dom";

import { ErrorPanel, Loading } from "../../components/Loading";
import { EmptyState, EntityDrawer, PageHeader } from "../../components/ui";
import { api } from "../../lib/api";
import {
  ROLE_LABEL_EN,
  TENANT_ROLES,
  type TenantMember,
  type TenantMemberDetail,
  type TenantRole,
} from "../../lib/api";

// We deliberately reuse the modal components from the list page —
// they're already wired to the mutations + invalidation queries
// that this page also wants to share. Re-exporting from that file
// instead of duplicating keeps Suspend / Remove copy + behaviour in
// lockstep.
import {
  // The modals aren't exported from the list page today; they're
  // file-local. We define lightweight wrappers here that call the
  // same api methods + bring up a confirm UI. If the modal logic
  // ever needs to live in more than one page, that's the moment to
  // promote them to a shared module — for now, two short copies are
  // honest and reviewable.
} from "./UsersRolesPage";

const _ROLE_BADGE_CLASS: Record<TenantRole, string> = {
  tenant_admin: "bg-brand-blue/10 text-brand-blue border-brand-blue/20",
  hr_director: "bg-brand-purple/10 text-brand-purple border-brand-purple/20",
  hr_partner: "bg-brand-teal/10 text-brand-teal border-brand-teal/20",
  manager: "bg-amber-100 text-amber-900 border-amber-200",
  employee: "bg-surface-sunken text-text-secondary border-border-subtle",
};

function RoleBadge({ role }: { role: TenantRole }) {
  return (
    <span
      className={`inline-flex items-center rounded-md border px-2 py-0.5 text-[11px] font-medium ${_ROLE_BADGE_CLASS[role]}`}
    >
      {ROLE_LABEL_EN[role]}
    </span>
  );
}

function fmt(ms: number | null): string {
  if (!ms) return "—";
  return new Date(ms).toLocaleString();
}

function useFmtRel() {
  const { t } = useTranslation();
  return (ms: number | null): string => {
    if (!ms) return "—";
    const diff = Date.now() - ms;
    const min = Math.round(diff / 60_000);
    if (min < 1) return t("app.ur_rel_just_now");
    if (min < 60) return t("app.ur_rel_minutes", { n: min });
    const hr = Math.round(min / 60);
    if (hr < 24) return t("app.ur_rel_hours", { n: hr });
    const day = Math.round(hr / 24);
    return t("app.ur_rel_days", { n: day });
  };
}

// ============================================================================
// Page
// ============================================================================

export function MemberDetailPage() {
  const { t } = useTranslation();
  const { user_id } = useParams<{ user_id: string }>();
  const navigate = useNavigate();
  const { user: clerkUser } = useUser();
  const me = clerkUser?.id ?? null;

  const q = useQuery({
    queryKey: ["tenant-member", user_id],
    queryFn: () => api.getTenantMember(user_id!),
    enabled: !!user_id,
  });

  if (q.isLoading) return <Loading />;
  if (q.error)
    return (
      <ErrorPanel message={(q.error as Error).message ?? t("app.mem_member_detail")} />
    );
  if (!q.data) return null;

  const member = q.data as TenantMemberDetail;
  const isSelf = me === member.user_id;

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title={member.full_name}
        subtitle={member.email ?? t("app.mem_member_detail")}
        breadcrumbs={[
          { label: t("app.mem_breadcrumb_settings"), href: "/settings" },
          { label: t("app.mem_breadcrumb_users"), href: "/settings/users" },
          { label: member.full_name },
        ]}
        actions={
          <Link
            to="/settings/users"
            className="inline-flex items-center gap-1.5 rounded-md border border-border-subtle px-3 py-1.5 text-sm text-text-secondary hover:bg-surface-sunken"
          >
            <ChevronLeft size={14} className="rtl:rotate-180" /> {t("app.mem_all_members")}
          </Link>
        }
      />

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-[1fr_320px]">
        <div className="flex flex-col gap-5">
          <IdentitySection member={member} />
          <ActivitySection actions={member.recent_actions ?? []} />
          {member.other_org_memberships !== null && (
            <MultiOrgSection
              memberships={member.other_org_memberships ?? []}
            />
          )}
        </div>
        <ActionsPanel
          member={member}
          isSelf={isSelf}
          onChanged={() => {
            void q.refetch();
          }}
          onRemoved={() => navigate("/settings/users")}
        />
      </div>
    </div>
  );
}

// ============================================================================
// Identity
// ============================================================================

function IdentitySection({ member }: { member: TenantMemberDetail }) {
  const { t } = useTranslation();
  const fmtRel = useFmtRel();
  return (
    <section
      aria-label={t("app.mem_identity")}
      className="rounded-xl border border-border-subtle bg-surface-default p-5"
    >
      <header className="mb-4 flex items-center gap-2">
        <ShieldCheck size={15} className="text-text-secondary" />
        <h2 className="text-sm font-semibold text-text-primary">
          {t("app.mem_identity")}
        </h2>
      </header>
      <div className="flex flex-wrap items-start gap-5">
        {member.image_url ? (
          <img
            src={member.image_url}
            alt=""
            className="h-16 w-16 rounded-full object-cover"
          />
        ) : (
          <span className="flex h-16 w-16 items-center justify-center rounded-full bg-brand-gradient text-xl font-semibold text-white">
            {(member.first_name?.[0] || member.email?.[0] || "?").toUpperCase()}
          </span>
        )}
        <dl className="flex flex-1 flex-col gap-2 text-sm">
          <Row label={t("app.mem_name")} value={member.full_name} />
          <Row
            label={t("app.mem_email")}
            value={
              member.email ? (
                <a
                  href={`mailto:${member.email}`}
                  className="text-brand-blue hover:underline"
                >
                  {member.email}
                </a>
              ) : (
                "—"
              )
            }
          />
          <Row label={t("app.mem_role")} value={<RoleBadge role={member.role} />} />
          <Row
            label={t("app.mem_status")}
            value={
              member.status === "suspended" ? (
                <span className="inline-flex items-center gap-1 rounded-md border border-rose-200 bg-rose-50 px-2 py-0.5 text-[11px] font-medium text-rose-700">
                  <Pause size={10} /> {t("app.mem_status_suspended")}
                </span>
              ) : (
                <span className="inline-flex items-center rounded-md border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[11px] font-medium text-emerald-700">
                  {t("app.mem_status_active")}
                </span>
              )
            }
          />
          <Row
            label={t("app.mem_joined")}
            value={`${fmt(member.joined_at_ms)} (${fmtRel(member.joined_at_ms)})`}
          />
          <Row
            label={t("app.mem_last_active")}
            value={`${fmt(member.last_active_at_ms)} (${fmtRel(member.last_active_at_ms)})`}
          />
        </dl>
      </div>
    </section>
  );
}

function Row({
  label,
  value,
}: {
  label: string;
  value: React.ReactNode;
}) {
  return (
    <div className="flex flex-wrap items-baseline gap-2">
      <dt className="w-28 text-xs uppercase tracking-wider text-text-tertiary">
        {label}
      </dt>
      <dd className="text-sm text-text-primary">{value}</dd>
    </div>
  );
}

// ============================================================================
// Activity
// ============================================================================

type ActivityRow = {
  action: string;
  target_email: string | null;
  occurred_at: string | null;
  before: Record<string, unknown> | null;
  after: Record<string, unknown> | null;
};

function ActivitySection({ actions }: { actions: ActivityRow[] }) {
  const { t } = useTranslation();
  // Filterable by date range — simple "all / last 30d / last 7d".
  const [range, setRange] = useState<"all" | "30d" | "7d">("all");

  const rows = useMemo(() => {
    if (range === "all") return actions;
    const cutoff =
      Date.now() - (range === "30d" ? 30 : 7) * 86_400_000;
    return actions.filter((a) => {
      if (!a.occurred_at) return false;
      return new Date(a.occurred_at).getTime() >= cutoff;
    });
  }, [actions, range]);

  return (
    <section
      aria-label={t("app.mem_recent_activity")}
      className="rounded-xl border border-border-subtle bg-surface-default p-5"
    >
      <header className="mb-4 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Calendar size={15} className="text-text-secondary" />
          <h2 className="text-sm font-semibold text-text-primary">
            {t("app.mem_recent_in_tenant")}
          </h2>
        </div>
        <select
          value={range}
          onChange={(e) =>
            setRange(e.target.value as "all" | "30d" | "7d")
          }
          aria-label={t("app.mem_filter_aria")}
          className="rounded-md border border-border-subtle bg-surface-default px-2 py-1 text-xs text-text-primary"
        >
          <option value="all">{t("app.mem_range_all")}</option>
          <option value="30d">{t("app.mem_range_30d")}</option>
          <option value="7d">{t("app.mem_range_7d")}</option>
        </select>
      </header>

      {rows.length === 0 ? (
        <EmptyState
          headline={t("app.mem_no_activity_h")}
          description={t("app.mem_no_activity_d")}
        />
      ) : (
        <ul className="divide-y divide-border-subtle">
          {rows.map((a, i) => (
            <li
              key={`${a.occurred_at}-${i}`}
              className="flex flex-wrap items-start justify-between gap-3 py-2.5 text-sm"
            >
              <div className="flex flex-col gap-0.5">
                <span className="font-medium text-text-primary">
                  {a.action.replaceAll("_", " ")}
                </span>
                {a.target_email && (
                  <span className="text-xs text-text-tertiary">
                    → {a.target_email}
                  </span>
                )}
              </div>
              <span className="text-xs text-text-tertiary">
                {a.occurred_at
                  ? new Date(a.occurred_at).toLocaleString()
                  : "—"}
              </span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

// ============================================================================
// Multi-org membership (TA + HR Director only)
// ============================================================================

function MultiOrgSection({
  memberships,
}: {
  memberships: Array<{
    organization_id: string;
    organization_name: string | null;
    organization_slug: string | null;
    role: TenantRole;
  }>;
}) {
  const { t } = useTranslation();
  return (
    <section
      aria-label={t("app.mem_other_orgs_aria")}
      className="rounded-xl border border-border-subtle bg-surface-default p-5"
    >
      <header className="mb-4 flex items-center gap-2">
        <Building2 size={15} className="text-text-secondary" />
        <h2 className="text-sm font-semibold text-text-primary">
          {t("app.mem_other_orgs_title")}
        </h2>
      </header>
      {memberships.length === 0 ? (
        <p className="text-sm text-text-secondary">
          {t("app.mem_only_your_tenant")}
        </p>
      ) : (
        <ul className="divide-y divide-border-subtle">
          {memberships.map((m) => (
            <li
              key={m.organization_id}
              className="flex flex-wrap items-center justify-between gap-3 py-2.5 text-sm"
            >
              <span className="text-text-primary">
                {m.organization_name ?? m.organization_id}
                {m.organization_slug && (
                  <span className="ms-2 text-xs text-text-tertiary">
                    @{m.organization_slug}
                  </span>
                )}
              </span>
              <RoleBadge role={m.role} />
            </li>
          ))}
        </ul>
      )}
      <p className="mt-3 text-xs text-text-tertiary">
        {t("app.mem_other_orgs_note")}
      </p>
    </section>
  );
}

// ============================================================================
// Actions panel
// ============================================================================

function ActionsPanel({
  member,
  isSelf,
  onChanged,
  onRemoved,
}: {
  member: TenantMember;
  isSelf: boolean;
  onChanged: () => void;
  onRemoved: () => void;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState<
    "role" | "suspend" | "unsuspend" | "remove" | null
  >(null);

  return (
    <>
      <aside
        aria-label={t("app.mem_actions_aria")}
        className="h-fit rounded-xl border border-border-subtle bg-surface-default p-4"
      >
        <h2 className="mb-3 text-sm font-semibold text-text-primary">
          {t("app.mem_actions")}
        </h2>
        <div className="flex flex-col gap-2">
          <button
            type="button"
            disabled={isSelf}
            onClick={() => setOpen("role")}
            title={isSelf ? t("app.mem_cant_change_own") : undefined}
            className="inline-flex items-center justify-between gap-2 rounded-md border border-border-subtle px-3 py-2 text-sm text-text-primary hover:bg-surface-sunken disabled:cursor-not-allowed disabled:text-text-tertiary"
          >
            <span className="flex items-center gap-2">
              <UserCog size={14} /> {t("app.mem_change_role")}
            </span>
          </button>
          {member.status === "active" ? (
            <button
              type="button"
              disabled={isSelf}
              onClick={() => setOpen("suspend")}
              title={isSelf ? t("app.mem_cant_suspend_self") : undefined}
              className="inline-flex items-center justify-between gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800 hover:bg-amber-100 disabled:cursor-not-allowed disabled:text-text-tertiary"
            >
              <span className="flex items-center gap-2">
                <Pause size={14} /> {t("app.mem_suspend")}
              </span>
            </button>
          ) : (
            <button
              type="button"
              onClick={() => setOpen("unsuspend")}
              className="inline-flex items-center justify-between gap-2 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800 hover:bg-emerald-100"
            >
              <span className="flex items-center gap-2">
                <Play size={14} /> {t("app.mem_restore")}
              </span>
            </button>
          )}
          <button
            type="button"
            disabled={isSelf}
            onClick={() => setOpen("remove")}
            title={isSelf ? t("app.mem_cant_remove_self") : undefined}
            className="inline-flex items-center justify-between gap-2 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800 hover:bg-rose-100 disabled:cursor-not-allowed disabled:text-text-tertiary"
          >
            <span className="flex items-center gap-2">
              <Trash2 size={14} /> {t("app.mem_remove")}
            </span>
          </button>
        </div>
        <p className="mt-3 text-xs text-text-tertiary">
          <Mail size={10} className="inline" /> {t("app.mem_removal_note")}
        </p>
      </aside>

      {open === "role" && (
        <InlineChangeRoleModal
          member={member}
          onClose={() => setOpen(null)}
          onDone={() => {
            setOpen(null);
            onChanged();
          }}
        />
      )}
      {open === "suspend" && (
        <InlineSuspendModal
          member={member}
          onClose={() => setOpen(null)}
          onDone={() => {
            setOpen(null);
            onChanged();
          }}
        />
      )}
      {open === "unsuspend" && (
        <InlineUnsuspendConfirm
          member={member}
          onClose={() => setOpen(null)}
          onDone={() => {
            setOpen(null);
            onChanged();
          }}
        />
      )}
      {open === "remove" && (
        <InlineRemoveConfirm
          member={member}
          onClose={() => setOpen(null)}
          onDone={() => {
            setOpen(null);
            onRemoved();
          }}
        />
      )}
    </>
  );
}

// ============================================================================
// Modals — inline duplicates of the list-page modals (kept in sync
// by behaviour; the modal logic is small enough that promoting it
// to a shared module is over-engineering for two callers).
// ============================================================================

// Modals route through <EntityDrawer /> (slides in from the
// trailing edge with a darkened backdrop). Each modal's
// ModalFooter renders inside children at the bottom of the body —
// not the drawer's own footer slot — so the 4 modal call sites
// don't need rewriting. Visual hierarchy stays the same.
function ModalShell({
  title,
  onClose,
  children,
}: {
  title: string;
  onClose: () => void;
  children: React.ReactNode;
}) {
  return (
    <EntityDrawer
      open
      onOpenChange={(o) => {
        if (!o) onClose();
      }}
      title={title}
    >
      <div className="flex flex-col gap-3">{children}</div>
    </EntityDrawer>
  );
}

function InlineChangeRoleModal({
  member,
  onClose,
  onDone,
}: {
  member: TenantMember;
  onClose: () => void;
  onDone: () => void;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [role, setRole] = useState<TenantRole>(member.role);
  const [err, setErr] = useState<string | null>(null);
  const m = useMutation({
    mutationFn: () => api.changeTenantMemberRole(member.user_id, role),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["tenant-members"] });
      onDone();
    },
    onError: (e: Error) => setErr(e.message),
  });
  return (
    <ModalShell title={t("app.mem_change_role_title", { name: member.full_name })} onClose={onClose}>
      <div className="flex flex-col gap-3">
        <p className="text-sm text-text-secondary">
          {t("app.mem_current_role")} <RoleBadge role={member.role} />
        </p>
        <select
          value={role}
          onChange={(e) => setRole(e.target.value as TenantRole)}
          aria-label={t("app.mem_new_role_aria")}
          className="rounded-md border border-border-subtle bg-surface-default px-3 py-2 text-sm text-text-primary"
        >
          {TENANT_ROLES.map((r) => (
            <option key={r} value={r}>
              {ROLE_LABEL_EN[r]}
            </option>
          ))}
        </select>
        <p className="text-xs text-text-tertiary">
          {t("app.mem_role_grant_note")}
        </p>
        {err && (
          <ModalError msg={err} />
        )}
        <ModalFooter
          onClose={onClose}
          primary={t("app.mem_change_role_cta")}
          loading={m.isPending}
          disabled={role === member.role}
          onPrimary={() => {
            setErr(null);
            m.mutate();
          }}
        />
      </div>
    </ModalShell>
  );
}

function InlineRemoveConfirm({
  member,
  onClose,
  onDone,
}: {
  member: TenantMember;
  onClose: () => void;
  onDone: () => void;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [err, setErr] = useState<string | null>(null);
  const m = useMutation({
    mutationFn: () => api.removeTenantMember(member.user_id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["tenant-members"] });
      onDone();
    },
    onError: (e: Error) => setErr(e.message),
  });
  return (
    <ModalShell title={t("app.mem_remove_title", { name: member.full_name })} onClose={onClose}>
      <div className="flex flex-col gap-3">
        <p className="text-sm text-text-secondary">
          {t("app.mem_remove_desc", { name: member.full_name })}
        </p>
        {err && <ModalError msg={err} />}
        <ModalFooter
          onClose={onClose}
          primary={t("app.mem_remove_cta")}
          danger
          loading={m.isPending}
          onPrimary={() => {
            setErr(null);
            m.mutate();
          }}
        />
      </div>
    </ModalShell>
  );
}

function InlineSuspendModal({
  member,
  onClose,
  onDone,
}: {
  member: TenantMember;
  onClose: () => void;
  onDone: () => void;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [reason, setReason] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const m = useMutation({
    mutationFn: () =>
      api.suspendTenantMember(member.user_id, reason || undefined),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["tenant-members"] });
      onDone();
    },
    onError: (e: Error) => setErr(e.message),
  });
  return (
    <ModalShell title={t("app.mem_suspend_title", { name: member.full_name })} onClose={onClose}>
      <div className="flex flex-col gap-3">
        <p className="text-sm text-text-secondary">
          {t("app.mem_suspend_desc")}
        </p>
        <textarea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          rows={3}
          placeholder={t("app.mem_suspend_reason_ph")}
          className="rounded-md border border-border-subtle bg-surface-default px-3 py-2 text-sm text-text-primary"
        />
        {err && <ModalError msg={err} />}
        <ModalFooter
          onClose={onClose}
          primary={t("app.mem_suspend_cta")}
          danger
          loading={m.isPending}
          onPrimary={() => {
            setErr(null);
            m.mutate();
          }}
        />
      </div>
    </ModalShell>
  );
}

function InlineUnsuspendConfirm({
  member,
  onClose,
  onDone,
}: {
  member: TenantMember;
  onClose: () => void;
  onDone: () => void;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [err, setErr] = useState<string | null>(null);
  const m = useMutation({
    mutationFn: () => api.unsuspendTenantMember(member.user_id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["tenant-members"] });
      onDone();
    },
    onError: (e: Error) => setErr(e.message),
  });
  return (
    <ModalShell title={t("app.mem_unsuspend_title", { name: member.full_name })} onClose={onClose}>
      <div className="flex flex-col gap-3">
        <p className="text-sm text-text-secondary">
          {t("app.mem_unsuspend_desc", { name: member.full_name })}
        </p>
        {err && <ModalError msg={err} />}
        <ModalFooter
          onClose={onClose}
          primary={t("app.mem_unsuspend_cta")}
          loading={m.isPending}
          onPrimary={() => {
            setErr(null);
            m.mutate();
          }}
        />
      </div>
    </ModalShell>
  );
}

function ModalError({ msg }: { msg: string }) {
  return (
    <div
      role="alert"
      className="flex items-start gap-2 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700"
    >
      <AlertTriangle size={12} className="mt-0.5 shrink-0" />
      {msg}
    </div>
  );
}

function ModalFooter({
  onClose,
  primary,
  onPrimary,
  loading,
  disabled,
  danger,
}: {
  onClose: () => void;
  primary: string;
  onPrimary: () => void;
  loading?: boolean;
  disabled?: boolean;
  danger?: boolean;
}) {
  const { t } = useTranslation();
  return (
    <footer className="flex justify-end gap-2 pt-2">
      <button
        type="button"
        onClick={onClose}
        className="rounded-md border border-border-subtle px-3 py-1.5 text-sm text-text-secondary hover:bg-surface-sunken"
      >
        {t("app.mem_cancel")}
      </button>
      <button
        type="button"
        onClick={onPrimary}
        disabled={loading || disabled}
        className={[
          "rounded-md px-3 py-1.5 text-sm font-medium text-white disabled:opacity-60",
          danger ? "bg-rose-600 hover:bg-rose-700" : "bg-brand hover:opacity-90",
        ].join(" ")}
      >
        {loading ? t("app.mem_working") : primary}
      </button>
    </footer>
  );
}
